import { z } from 'zod';
import { DeclarationType } from '@dealstack/shared/lib/generated';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export enum DeclarationsFormField {
	BENEFICIAL_OWNERSHIP = 'beneficialOwnership',
	SOURCE_OF_FUNDS = 'sourceOfFunds',
	SOURCE_OF_FUNDS_OTHER_DETAILS = 'sourceOfFundsOtherDetails',
	IS_POLITICALLY_EXPOSED_PERSON = 'isPoliticallyExposedPerson',
	PEP_DETAILS = 'pepDetails',
}

interface DeclarationsRequired {
	isBeneficialOwnershipRequired: boolean;
	isSourceOfFundsRequired: boolean;
	isPepRequired: boolean;
}

export const declarationsEditFormShape = (
	{ isBeneficialOwnershipRequired, isSourceOfFundsRequired, isPepRequired }: DeclarationsRequired,
	tCommonForm: TranslateFunction<I18Namespaces.COMMON_FORM>
) =>
	z
		.object({
			[DeclarationsFormField.BENEFICIAL_OWNERSHIP]: z.boolean(),
			[DeclarationsFormField.SOURCE_OF_FUNDS]: z.array(z.nativeEnum(DeclarationType)),
			[DeclarationsFormField.SOURCE_OF_FUNDS_OTHER_DETAILS]: z.string(),
			[DeclarationsFormField.IS_POLITICALLY_EXPOSED_PERSON]: z.boolean().optional(),
			[DeclarationsFormField.PEP_DETAILS]: z.string(),
		})
		.superRefine((values, ctx) => {
			if (isBeneficialOwnershipRequired && values.beneficialOwnership !== true) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [DeclarationsFormField.BENEFICIAL_OWNERSHIP],
					message: tCommonForm('consent_required'),
				});
			}
			if (isSourceOfFundsRequired && values.sourceOfFunds.length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [DeclarationsFormField.SOURCE_OF_FUNDS],
					message: tCommonForm('field_required'),
				});
			}
			if (
				isSourceOfFundsRequired &&
				values.sourceOfFunds.includes(DeclarationType.SOURCE_OF_FUNDS_OTHER) &&
				!values.sourceOfFundsOtherDetails.trim()
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [DeclarationsFormField.SOURCE_OF_FUNDS_OTHER_DETAILS],
					message: tCommonForm('field_required'),
				});
			}
			if (isPepRequired && values.isPoliticallyExposedPerson === undefined) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [DeclarationsFormField.IS_POLITICALLY_EXPOSED_PERSON],
					message: tCommonForm('field_required'),
				});
			}
			if (
				isPepRequired &&
				values.isPoliticallyExposedPerson === true &&
				!values.pepDetails.trim()
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [DeclarationsFormField.PEP_DETAILS],
					message: tCommonForm('field_required'),
				});
			}
		});

export type DeclarationsEditFormValues = z.infer<ReturnType<typeof declarationsEditFormShape>>;
